import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateInsightsIllustration, EmptyStateOnboarding, Callout } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { OctoLink } from "@octopusdeploy/utilities";
import { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import RequestFreeTrialUpsellCallout from "~/components/Callout/RequestFreeTrialUpsellCallout";
interface LoaderData {
    hasFeatureRecentlyExpired: boolean;
}
export async function insightsReportUpsellDataLoader(repository: Repository) {
    const licenseCheck = await repository.InsightsReports.getInsightsLicenseCheck();
    return { hasFeatureRecentlyExpired: licenseCheck.HasFeatureRecentlyExpired };
}
type InsightsReportUpsellProps = {
    loaderData: LoaderData;
};
export function InsightsReportUpsellPage({ loaderData }: InsightsReportUpsellProps) {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const onRequestTrial = async (repository: Repository) => {
        await repository.InsightsReports.requestTrial();
    };
    const trackOpeningRequestDialog = () => dispatchAction("Request Insights trial dialog opened");
    useEffect(() => {
        dispatchAction("View Insights Reports Upsell page");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const description = (<>
            {loaderData.hasFeatureRecentlyExpired && <LicenseExpiredCallout />}
            <span>Space-level DevOps Insights gives you a unified view of DORA metrics (deployment speed, failure rates, and recovery times) across projects, so you can see what’s slowing deployments and optimize at scale.</span>
        </>);
    const insightsDocsLink = OctoLink.Create("SpaceLevelDevOpsInsights");
    const calloutContent = (<div className={styles.calloutContent}>
            <p>Request a free trial of space-level DevOps Insights.</p>
            <p>Or, view project-level DevOps Insights by opening a project and selecting Insights from the navigation.</p>
        </div>);
    const primaryAction: PrimaryPageAction = {
        type: "custom",
        key: "request-trial",
        content: (<RequestFreeTrialUpsellCallout featureName={"Space-level DevOps Insights"} onRequestTrial={onRequestTrial} onTrialRequested={() => dispatchAction("Request for Insights trial submitted")} docsLink={insightsDocsLink} docsLabel={"Docs: Space-level DevOps Insights"} dialogTitle={"Request a free trial of space-level DevOps Insights"} trackOpeningRequestDialog={trackOpeningRequestDialog}>
                {calloutContent}
            </RequestFreeTrialUpsellCallout>),
    };
    return <EmptyStateOnboarding title={"See deployment trends across projects with space-level DevOps Insights"} image={<EmptyStateInsightsIllustration />} primaryAction={primaryAction} description={description}/>;
}
function LicenseExpiredCallout() {
    return (<Callout title="License expired" type={"information"}>
            <p>This feature is limited to the Enterprise tier.</p>
        </Callout>);
}
const styles = {
    calloutContent: css({
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: space[6],
        "& p": {
            margin: 0,
        },
    }),
};
